// src/utils/queueMetrics.js

import { getIncidentSeverity, SEVERITY_IDS } from "../constants/severity";
import { isActiveStatus } from "../constants/incidentStatus";
import { millisSince } from "./duration";

/**
 * Operational facts derived from a set of incidents.
 *
 * Counts and elapsed times only. Severity deliberately carries no published
 * response-time target (see constants/severity.js), so nothing here counts down
 * towards a breach we have not committed to. The numbers state what is true now
 * and let the reader draw the conclusion.
 *
 * A cell that has nothing to report returns null rather than 0, so the strip can
 * omit it instead of rendering a placeholder.
 */

/** Milliseconds since the incident was reported. */
export function incidentAgeMs(problem) {
  return millisSince(problem?.createdAt);
}

/**
 * An expert owns this incident but has not spoken yet.
 *
 * Reads the denormalised `firstResponseAt` stamp on the problem rather than the
 * event stream, because a dashboard cannot afford one subcollection read per
 * incident just to render a header.
 */
export function isAwaitingFirstResponse(problem) {
  if (!problem?.helperId) return false;
  if (!isActiveStatus(problem?.status)) return false;
  return !problem.firstResponseAt;
}

/** Largest value in a list, ignoring nulls. Returns null for an empty list. */
function maxOrNull(values) {
  const present = values.filter((value) => typeof value === "number");
  return present.length ? Math.max(...present) : null;
}

/**
 * @param {object[]} problems Every incident the viewer can see, any status.
 * @returns {object} Raw counts. Callers decide which ones matter to their role.
 */
export function summarizeQueue(problems = []) {
  const active = problems.filter((p) => isActiveStatus(p.status));
  const unanswered = active.filter(isAwaitingFirstResponse);

  return {
    activeCount: active.length,
    resolvedCount: problems.filter((p) => p.status === "completed").length,
    unresolvedCount: problems.filter((p) => p.status === "unresolved").length,

    /** Reported but nobody has claimed it yet. */
    unclaimedCount: active.filter((p) => p.status === "open").length,

    p1Count: active.filter(
      (p) => getIncidentSeverity(p).id === SEVERITY_IDS.P1
    ).length,

    awaitingFirstResponseCount: unanswered.length,

    oldestActiveMs: maxOrNull(active.map(incidentAgeMs)),
    oldestUnansweredMs: maxOrNull(unanswered.map(incidentAgeMs)),

    // Set by the expert in the workspace. Surfacing it on the dashboard is the
    // whole point of the field: either party can tell at a glance whether they
    // are the one holding up their own incident.
    waitingOnClientCount: active.filter((p) => p.nextActionOwner === "client")
      .length,
    waitingOnExpertCount: active.filter((p) => p.nextActionOwner === "expert")
      .length,
  };
}

/**
 * How many incidents are waiting on the person reading the screen.
 *
 * @param {object} summary Result of summarizeQueue.
 * @param {"client"|"expert"} viewerRole
 */
export function waitingOnViewerCount(summary, viewerRole) {
  return viewerRole === "client"
    ? summary.waitingOnClientCount
    : summary.waitingOnExpertCount;
}
